import { ModalProps, Steps, message } from 'antd';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Description from 'views/Conversation/Sider/Description';
import { toPng } from 'html-to-image';
import { resourceURLToFile } from 'utils/getImage';
import domesticLogisticsSvg from '../../assets/domesticLogistics.svg';
import logisticsCompanySvg from '../../assets/logisticsCompany.svg';
import shipmentNumberSvg from '../../assets/shipmentNumber.svg';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { useSendFileImage } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ImageUpload';
import { JustRightHeightModal } from 'components/styled/scoped';
import { patchTranslatedResult } from 'views/Conversation/Sider/useTradeTranslate';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import DOMPurify from 'dompurify';
import { stripHtmlTag } from 'utils/stripHtmlTag';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { useRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';
import { regionTimeZoneMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';

export type ScreenshotTrackingInfo = {
  updateTime?: string | number;
  description: string;
} & Record<string, unknown>;

export type ScreenshotLogisticsPackage = {
  trackingNumber?: string;
  shippingCarrier?: string;
  firstMileTrackingNumber?: string;
  trackingInfo: ScreenshotTrackingInfo[];
} & Record<string, unknown>;

interface ScreenshotModalProps extends ModalProps {
  platform?: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
  conversation?: Conversation.Conversation;
  packages: ScreenshotLogisticsPackage[];
  onClose: () => void;
}

export const ScreenshotModal = (props: ScreenshotModalProps) => {
  const { platform, region, conversation, packages, onClose, ...modalProps } = props;
  const { t } = useTranslate();
  const ref = useRef<HTMLDivElement>(null);

  const sendFileImage = useSendFileImage(conversation);
  const [sending, setSending] = useState(false);

  const [dataUrl, setDataUrl] = useState('');

  const handleSend = useMemoizedFn(async () => {
    try {
      setSending(true);
      const file = await resourceURLToFile(dataUrl);
      await sendFileImage(file);
      onClose();
    } catch (err) {
      message.error(t('common.feedback.operationFailed'));
    } finally {
      setSending(false);
    }
  });

  const regionLangMap = useRegionLanguageMap();

  const lang = regionLangMap[region || 'US'];

  const [translatedPackages, setTranslatedPackages] = useState<ScreenshotLogisticsPackage[]>();

  const refsCurrent = { t };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (!isTiKTokUpperCase(platform)) return;

    const originTextList = packages
      .reduce<string[]>((res, pack) => {
        const { trackingInfo } = pack;

        return trackingInfo.reduce((res, track) => {
          const { description } = track;
          let text = description;
          try {
            text = DOMPurify.sanitize(decodeURIComponent(description), {
              RETURN_DOM: true,
            }).innerText;
          } catch (error) {
            //
          }

          res.push(text);

          return res;
        }, res);
      }, [])
      .map(stripHtmlTag);

    overseaTranslate(originTextList, lang, 'side')
      .then((translatedList) => {
        const resList = patchTranslatedResult(translatedList, originTextList);

        let flag = 0;

        const translatedPackages = packages.map((logistics) => {
          const { trackingInfo, ...others } = logistics;

          const nextTrackingInfo = trackingInfo.map((info) => {
            const result = {
              ...info,
              description: resList[flag],
            };

            flag += 1;

            return result;
          });

          return {
            ...others,
            trackingInfo: nextTrackingInfo,
          };
        });

        setTranslatedPackages(translatedPackages);
      })
      .catch(() => {
        message.error(refs.current.t('common.feedback.translationFailed'));
      });
  }, [platform, packages, lang]);

  const content = useMemo(() => {
    const timeZone = regionTimeZoneMap[toLowerCase(region)];
    return (isTiKTokUpperCase(platform) ? translatedPackages : packages)?.map(
      (logisticsPackage) => {
        const { trackingNumber, shippingCarrier, firstMileTrackingNumber, trackingInfo } =
          logisticsPackage;

        return (
          <div className="flex flex-col gap-1">
            {trackingNumber && (
              <div className="flex gap-2">
                <img src={shipmentNumberSvg} alt="" />
                {trackingNumber}
              </div>
            )}
            {shippingCarrier && (
              <div className="flex gap-2">
                <img src={logisticsCompanySvg} alt="" />
                {shippingCarrier}
              </div>
            )}
            {firstMileTrackingNumber && (
              <div className="flex gap-2">
                <img src={domesticLogisticsSvg} alt="" />
                {firstMileTrackingNumber}
              </div>
            )}
            {
              <Steps progressDot direction="vertical" current={0}>
                {trackingInfo.slice(0, 5).map(({ updateTime, description }) => {
                  const validUpdateTime = toNumber(updateTime);
                  return (
                    <Steps.Step
                      key={updateTime || description}
                      title={<Description text={description} />}
                      description={
                        validUpdateTime
                          ? dayjs.tz(validUpdateTime * 1000, timeZone).format('YYYY-MM-DD HH:mm')
                          : undefined
                      }
                    />
                  );
                })}
              </Steps>
            }
          </div>
        );
      },
    );
  }, [packages, platform, translatedPackages, region]);

  useLayoutEffect(() => {
    if (ref.current && content) {
      toPng(ref.current, { pixelRatio: 1 })
        .then((dataUrl) => {
          setDataUrl(dataUrl);
        })
        .catch((err) => {
          console.log(err);
          message.error(refs.current.t('common.feedback.operationFailed'));
        });
    }
  }, [content]);

  return (
    <JustRightHeightModal
      width={640}
      bodyStyle={{ padding: 0 }}
      centered
      title={t('scene.chatWorkspace.orderTab.logistics.action.sendLogisticsDetails')}
      okText={t('common.action.send')}
      onOk={handleSend}
      okButtonProps={{ loading: sending }}
      onCancel={() => onClose()}
      {...modalProps}
    >
      <img className="w-full" src={dataUrl} alt="" />
      <div className="absolute -top-[99999px] -left-[99999px] w-[100%]">
        <div ref={ref} className="bg-fill-0 p-6">
          {content}
        </div>
      </div>
    </JustRightHeightModal>
  );
};
